/**
 * UI Slice
 * Manages global UI state (sidebar, notifications, modals, loading states, system settings)
 */

import { createSlice } from '@reduxjs/toolkit';
import { LOCALE_CONFIG } from '../../shared/config/env';

const initialState = {
  // Sidebar state
  sidebarOpen: false,
  sidebarCollapsed: localStorage.getItem('sidebar_collapsed') === 'true',

  // Theme
  theme: localStorage.getItem('theme') || 'dark',

  // System Settings (Currency, Locale, etc.)
  systemSettings: {
    country: localStorage.getItem('system_country') || LOCALE_CONFIG.DEFAULT_COUNTRY,
    currency: localStorage.getItem('system_currency') || LOCALE_CONFIG.DEFAULT_CURRENCY,
    locale: localStorage.getItem('system_locale') || LOCALE_CONFIG.DEFAULT_LOCALE,
    timezone: localStorage.getItem('system_timezone') || LOCALE_CONFIG.DEFAULT_TIMEZONE,
    dateFormat: localStorage.getItem('system_date_format') || 'MM/DD/YYYY',
    language: localStorage.getItem('system_language') || 'English (US)',
  },

  // Notifications
  notifications: [],

  // Global loading states
  globalLoading: false,
  loadingMessage: '',

  // Modals
  activeModal: null,
  modalData: null,

  // Toast notifications
  toasts: [],
};

const uiSlice = createSlice({
  name: 'ui',
  initialState,
  reducers: {
    // Sidebar actions
    toggleSidebar: (state) => {
      state.sidebarOpen = !state.sidebarOpen;
    },
    setSidebarOpen: (state, action) => {
      state.sidebarOpen = action.payload;
    },
    toggleSidebarCollapsed: (state) => {
      state.sidebarCollapsed = !state.sidebarCollapsed;
      localStorage.setItem('sidebar_collapsed', state.sidebarCollapsed);
    },

    // Theme actions
    setTheme: (state, action) => {
      state.theme = action.payload;
      localStorage.setItem('theme', action.payload);
    },

    // System Settings actions
    setSystemCountry: (state, action) => {
      state.systemSettings.country = action.payload;
      localStorage.setItem('system_country', action.payload);
    },
    setSystemCurrency: (state, action) => {
      state.systemSettings.currency = action.payload;
      localStorage.setItem('system_currency', action.payload);
    },
    setSystemLocale: (state, action) => {
      state.systemSettings.locale = action.payload;
      localStorage.setItem('system_locale', action.payload);
    },
    setSystemTimezone: (state, action) => {
      state.systemSettings.timezone = action.payload;
      localStorage.setItem('system_timezone', action.payload);
    },
    setSystemDateFormat: (state, action) => {
      state.systemSettings.dateFormat = action.payload;
      localStorage.setItem('system_date_format', action.payload);
    },
    setSystemLanguage: (state, action) => {
      state.systemSettings.language = action.payload;
      localStorage.setItem('system_language', action.payload);
    },
    updateSystemSettings: (state, action) => {
      // Bulk update multiple settings
      const updates = action.payload;
      Object.keys(updates).forEach((key) => {
        if (state.systemSettings[key] !== undefined) {
          state.systemSettings[key] = updates[key];
          localStorage.setItem(`system_${key}`, updates[key]);
        }
      });
    },

    // Notification actions
    addNotification: (state, action) => {
      state.notifications.unshift({
        id: Date.now(),
        timestamp: new Date().toISOString(),
        read: false,
        ...action.payload,
      });

      // Keep only last 50 notifications
      if (state.notifications.length > 50) {
        state.notifications = state.notifications.slice(0, 50);
      }
    },
    markNotificationRead: (state, action) => {
      const notification = state.notifications.find((n) => n.id === action.payload);
      if (notification) {
        notification.read = true;
      }
    },
    markAllNotificationsRead: (state) => {
      state.notifications.forEach((n) => (n.read = true));
    },
    clearNotification: (state, action) => {
      state.notifications = state.notifications.filter((n) => n.id !== action.payload);
    },
    clearAllNotifications: (state) => {
      state.notifications = [];
    },

    // Global loading actions
    setGlobalLoading: (state, action) => {
      state.globalLoading = action.payload.loading;
      state.loadingMessage = action.payload.message || '';
    },

    // Modal actions
    openModal: (state, action) => {
      state.activeModal = action.payload.modalId;
      state.modalData = action.payload.data || null;
    },
    closeModal: (state) => {
      state.activeModal = null;
      state.modalData = null;
    },

    // Toast actions
    addToast: (state, action) => {
      const toast = {
        id: Date.now(),
        type: 'info', // info, success, warning, error
        duration: 5000,
        ...action.payload,
      };
      state.toasts.push(toast);
    },
    removeToast: (state, action) => {
      state.toasts = state.toasts.filter((t) => t.id !== action.payload);
    },
    clearAllToasts: (state) => {
      state.toasts = [];
    },
  },
});

// Selectors
export const selectSidebarOpen = (state) => state.ui.sidebarOpen;
export const selectSidebarCollapsed = (state) => state.ui.sidebarCollapsed;
export const selectTheme = (state) => state.ui.theme;
export const selectSystemSettings = (state) => state.ui.systemSettings;
export const selectSystemCountry = (state) => state.ui.systemSettings.country;
export const selectSystemCurrency = (state) => state.ui.systemSettings.currency;
export const selectSystemLocale = (state) => state.ui.systemSettings.locale;
export const selectSystemTimezone = (state) => state.ui.systemSettings.timezone;
export const selectSystemDateFormat = (state) => state.ui.systemSettings.dateFormat;
export const selectSystemLanguage = (state) => state.ui.systemSettings.language;
export const selectNotifications = (state) => state.ui.notifications;
export const selectUnreadNotificationCount = (state) =>
  state.ui.notifications.filter((n) => !n.read).length;
export const selectGlobalLoading = (state) => state.ui.globalLoading;
export const selectLoadingMessage = (state) => state.ui.loadingMessage;
export const selectActiveModal = (state) => state.ui.activeModal;
export const selectModalData = (state) => state.ui.modalData;

export const selectToasts = (state) => state.ui.toasts;

export const {
  toggleSidebar,
  setSidebarOpen,
  toggleSidebarCollapsed,
  setTheme,
  setSystemCountry,
  setSystemCurrency,
  setSystemLocale,
  setSystemTimezone,
  setSystemDateFormat,
  setSystemLanguage,
  updateSystemSettings,
  addNotification,
  markNotificationRead,
  markAllNotificationsRead,
  clearNotification,
  clearAllNotifications,
  setGlobalLoading,
  openModal,
  closeModal,
  addToast,
  removeToast,
  clearAllToasts,
} = uiSlice.actions;

export default uiSlice.reducer;
